//Database text - State machine should be renamed to StateOne / StateTwo ... etc
export const APPLIED = "applied";
export const CONFIRMED = "confirmed";
export const RECRUITERINTERVIEW = "recruiterinterview";
export const HIRINGMANAGERINTERVIEW = "hiringmanagerinterview";
export const DONE = "done";

export type JobState =
	| typeof APPLIED
	| typeof CONFIRMED
	| typeof RECRUITERINTERVIEW
	| typeof HIRINGMANAGERINTERVIEW
	| typeof DONE;

// User Text
export const xlate = {
	[APPLIED]: "Apply",
	[CONFIRMED]: "Phone Screen",
	[RECRUITERINTERVIEW]: "Interviewing",
	[HIRINGMANAGERINTERVIEW]: "Post Interview",
	[DONE]: "Archive"
};

//For Enum Mappings
export const JobStatesValues = Object.values({
	APPLIED,
	CONFIRMED,
	RECRUITERINTERVIEW,
	HIRINGMANAGERINTERVIEW,
	DONE
}) as JobState[];

// Types
export interface UserUsageStats {
	dbFilesReadCount: number;
	aiInputTokens: number;
	aiOutputTokens: number;
	aiCostEstimate: number;
	clientLoginCount: number;
	trialCoverLetters: number;
	trialResumes: number;
}

export interface UserDetails {
	userId: number;
	userIdentityPlatformId: string;
	name: string;
	email: string;
	phone?: string;
	campaign: string;
	title?: string;
	address?: string;
	city?: string;
	state?: string;
	zipCode?: string;
	linkedInUrl?: string;
	usageStats: UserUsageStats;
	userIdentityPlatformProviders: string[];
	trialCoverLettersRemaining: number;
	trialResumesRemaining: number;
}

// This data type is local to the client and extends JobCardApiData, without the jobId field
export interface JobCardData {
	id: string;
	placeholderId?: string;
	title: string;
	creationTimestamp: string;
	company: string;
	state: JobState;
	isError: boolean;
	isPending: boolean;
}

export interface JobDetails extends JobCardData {
	jobDescription: string;
	coverLetterFileId: number;
	coverLetter: string; //The Coverletter
	isGeneratingCoverLetter: boolean; //is Server Generating Coverletter
	resume: string;
	isGeneratingResume: boolean;
}

// ---- Begin History Event Types ----

export type JobEventType =
	| "job.create"
	| "job.change_state"
	| "job.archive"
	| "job.note"
	| "resume.generate"
	| "cover_letter.generate"
	| "cover_letter.edit"
	| "contact.add"
	| "contact.remove"
	| "thank_you_letter.generate"
	| "ghosting_letter.generate";

/** Base fields shared by every history event */
interface JobEventBase {
	eventId: number;
	jobId: number;
	eventTimestamp: string;
}

/** Discriminated union — eventType narrows eventData automatically */
export type JobEvent =
	| (JobEventBase & { eventType: "job.create"; eventData: JobCreateEventData })
	| (JobEventBase & {
			eventType: "job.change_state";
			eventData: JobChangeStateEventData;
	  })
	| (JobEventBase & {
			eventType: "resume.generate";
			eventData: ResumeGenerateEventData;
	  })
	| (JobEventBase & {
			eventType: "cover_letter.generate";
			eventData: CoverLetterGenerateEventData;
	  })
	| (JobEventBase & {
			eventType: "cover_letter.edit";
			eventData: CoverLetterEditEventData;
	  })
	| (JobEventBase & {
			eventType: "contact.add";
			eventData: ContactAddEventData;
	  })
	| (JobEventBase & {
			eventType: "contact.remove";
			eventData: ContactRemoveEventData;
	  })
	| (JobEventBase & {
			eventType: "thank_you_letter.generate";
			eventData: ThankYouLetterGenerateEventData;
	  })
	| (JobEventBase & {
			eventType: "ghosting_letter.generate";
			eventData: GhostingLetterGenerateEventData;
	  })
	| (JobEventBase & { eventType: "job.note"; eventData: JobNoteEventData })
	| (JobEventBase & {
			eventType: "job.archive";
			eventData: JobArchiveEventData;
	  });

export interface JobCreateEventData {
	company: string;
	title: string;
	initialState: JobState;
	descriptionFileId: number;
}

export interface JobChangeStateEventData {
	fromState: JobState;
	toState: JobState;
	reason?: string;
}

export interface JobNoteEventData {
	noteId: number;
	text: string;
}

export type ArchiveOutcome =
	| "offer_accepted"
	| "offer_declined"
	| "withdrawn"
	| "ghosted";

export interface JobArchiveEventData {
	fromState: JobState;
	reason?: string;
	outcome?: ArchiveOutcome;
}

export const outcomeLabels: Record<ArchiveOutcome, string> = {
	offer_accepted: "Offer Accepted",
	offer_declined: "Offer Declined",
	withdrawn: "Withdrawn",
	ghosted: "Ghosted"
};

export interface ResumeGenerateEventData {
	fileId: number;
}

export interface CoverLetterGenerateEventData {
	fileId: number;
}

export interface CoverLetterEditEventData {
	fileId: number;
}

export interface ContactAddEventData {
	contactId: number;
	contactName: string;
	contactRole?: string;
	contactEmail?: string;
	contactLinkedIn?: string;
}

export interface ContactRemoveEventData {
	contactId: number;
	contactName: string;
	contactRole?: string;
}

export interface ThankYouLetterGenerateEventData {
	fileId: number;
	contactId: number;
	recipientName: string;
	recipientRole?: string;
	letterContent: string;
}

export interface GhostingLetterGenerateEventData {
	fileId: number;
	contactId: number;
	recipientName: string;
	recipientRole?: string;
	letterContent: string;
	daysSinceLastContact?: number;
}

// ---- End History Event Types ----

// ---- Referral Types ----

export type ValidateReferralResponse =
	| { valid: true; referralType: "friend" | "group"; originatorName: string }
	| { valid: false; reason: ReferralInvalidReason };

export type ReferralInvalidReason =
	| "expired"
	| "redeemed"
	| "revoked"
	| "not_found";

export interface MyReferralCode {
	code: string;
	referralType: "friend" | "group";
	status: "pending" | "redeemed" | "active" | "expired" | "revoked";
	label: string | null;
	recipientContact: string | null;
	recipientName: string | null;
	maxUses: number;
	currentUses: number;
	createdAt: string;
	expiresAt: string;
	totalCreditsEarned: number;
	pendingPhaseCount: number;
}

export interface CreditBalance {
	balance: number;
	totalEarned: number;
	pendingPhases: PendingPhase[];
}

export interface PendingPhase {
	recipientContact: string;
	nextPhase: number;
	trigger: string;
	credits: number;
}

export interface PhaseStatus {
	phase: 1 | 2 | 3;
	status: "awarded" | "pending" | "forfeited";
}

export interface CatalogItem {
	itemKey: string;
	displayName: string;
	description: string;
	creditCost: number;
}

export interface MyReferrerInfo {
	referrerName: string | null;
	referralCode?: string;
	earnedPhases?: number;
	remainingPhases?: number;
	totalPendingCredits?: number;
}

export interface OriginatorSummary {
	activeCodeCount: number;
	totalRedemptions: number;
	creditsEarned: number;
	creditsPending: number;
	referralNames: string[];
}

export interface CreateReferralPayload {
	type: "friend" | "group";
	recipientName?: string;
	recipientContact?: string;
	recipientRole?: string;
	recipientLinkedIn?: string;
	label?: string;
	maxUses?: number;
	durationDays?: number;
}

export interface CreateReferralResponse {
	code: string;
	shareLink: string;
	expiresAt: string;
}

export interface RedeemReferralResponse {
	redeemerCredits: number;
	originatorName: string;
}

export interface PurchaseResponse {
	purchaseId: number;
	creditsSpent: number;
	remainingBalance: number;
	status: string;
}

export interface MailingAddress {
	fullName: string;
	street: string;
	city: string;
	state: string;
	zipCode: string;
	country: string;
}

// ---- End Referral Types ----

export interface ResumeProfile extends ResumeData {
	name?: string;
	email?: string;
	phone?: string;
	title?: string;
	address?: string;
	city?: string;
	state?: string;
	zipCode?: string;
	linkedInUrl?: string;
	portfolioUrl?: string;
}

export interface ResumeData {
	resumeMarkDown: string;
}

export interface SnackbarProps {
	theme: "light" | "dark";
	onGoAway?: () => void;
}
export interface SnackbarConfig {
	id: string;
	component: React.ComponentType<SnackbarProps>;
	theme: "light" | "dark";
	duration?: number;
}

// Re-export API types for backwards compatibility
export * from "./ApiTypes";
